import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  serverTimestamp,
  setDoc,
  Timestamp,
  deleteDoc,
  limit,
} from '@firebase/firestore';
import { db } from './firebase';
import { COLLECTIONS } from './collections';
import { InsuranceChecklist, StakeholderType, ChecklistProgress, ChecklistItem } from '../types';
import { mapDocs, mapDoc } from '@utils/firestore';
import { AppError } from '../utils/errorHandler';
import { InsuranceChecklistSchema, ChecklistProgressSchema } from '../schemas/firestoreSchemas';
import { structureClaimsApi } from './structureClaims';

export type { ChecklistProgress } from '../types';

type CompletedItemEntry = ChecklistProgress['completedItems'][number];

// Build a completed-item entry with optional fields stripped. Firestore rejects
// `undefined` fields unless the SDK is configured with ignoreUndefinedProperties,
// and FieldValue sentinels (serverTimestamp, etc.) can't live inside arrays —
// so we use a client Timestamp here.
export function buildCompletedItemEntry(
  itemId: string,
  photoUrl?: string,
  photoPath?: string,
  notes?: string
): CompletedItemEntry {
  const entry: CompletedItemEntry = { itemId, completedAt: Timestamp.now() };
  if (photoUrl !== undefined) entry.photoUrl = photoUrl;
  if (photoPath !== undefined) entry.photoPath = photoPath;
  if (notes !== undefined) entry.notes = notes;
  return entry;
}

export const checklistsApi = {
  async getChecklists(): Promise<InsuranceChecklist[]> {
    const q = query(
      collection(db, COLLECTIONS.INSURANCE_CHECKLISTS),
      orderBy('insurerName', 'asc')
    );

    const snapshot = await getDocs(q);
    return mapDocs(snapshot, InsuranceChecklistSchema);
  },

  async getChecklist(checklistId: string): Promise<InsuranceChecklist | null> {
    const docRef = doc(db, COLLECTIONS.INSURANCE_CHECKLISTS, checklistId);
    const snapshot = await getDoc(docRef);
    return mapDoc(snapshot, InsuranceChecklistSchema);
  },

  // Get default checklist (used when a structure has no insurer assigned).
  // Matches on `isDefault: true`, optionally filtered by stakeholder type.
  async getDefaultChecklist(stakeholderType?: StakeholderType): Promise<InsuranceChecklist | null> {
    // No stakeholder type (e.g. an unclaimed structure) resolves as
    // homeowner — without the filter, [0] of the unordered result picks
    // arbitrarily among the three per-type defaults.
    const constraints = [
      where('isDefault', '==', true),
      where('stakeholderType', '==', stakeholderType ?? 'homeowner'),
    ];
    const q = query(collection(db, COLLECTIONS.INSURANCE_CHECKLISTS), ...constraints);
    const snapshot = await getDocs(q);
    return mapDocs(snapshot, InsuranceChecklistSchema)[0] ?? null;
  },

  // Resolve the checklist a structure is working against: the one assigned at
  // claim time, else the default for the claim's stakeholder type. Same logic
  // as checklistStore.fetchChecklistForStructure but store-free, so callers
  // like post-create don't clobber the store's selectedChecklist.
  async getChecklistForStructure(structureId: string): Promise<InsuranceChecklist | null> {
    const claim = await structureClaimsApi.getClaim(structureId);
    if (claim?.insurerChecklistId) {
      const checklist = await this.getChecklist(claim.insurerChecklistId);
      if (checklist) return checklist;
    }
    return this.getDefaultChecklist(claim?.stakeholderType);
  },

  // List checklists available for a given stakeholder type, optionally filtered
  // to lists scoped to a service area (or with no scoping at all).
  // Sorted client-side to avoid needing a composite index for a ~10-doc set.
  async getChecklistsFor(params: {
    stakeholderType: StakeholderType;
    serviceAreaId?: string;
  }): Promise<InsuranceChecklist[]> {
    const q = query(
      collection(db, COLLECTIONS.INSURANCE_CHECKLISTS),
      where('stakeholderType', '==', params.stakeholderType)
    );
    const snapshot = await getDocs(q);
    const all = mapDocs(snapshot, InsuranceChecklistSchema).sort((a, b) =>
      a.insurerName.localeCompare(b.insurerName)
    );
    if (!params.serviceAreaId) return all;
    // Include lists that are either unscoped (no serviceAreaIds) or that
    // include this service area.
    return all.filter(
      (c) => !c.serviceAreaIds?.length || c.serviceAreaIds.includes(params.serviceAreaId!)
    );
  },

  // List all checklists belonging to a given insurer. Used by the agent management UI.
  // Sorted client-side to avoid a composite index (same pattern as getChecklistsFor).
  async getChecklistsByInsurer(insurerName: string): Promise<InsuranceChecklist[]> {
    const q = query(
      collection(db, COLLECTIONS.INSURANCE_CHECKLISTS),
      where('insurerName', '==', insurerName)
    );
    const snapshot = await getDocs(q);
    return mapDocs(snapshot, InsuranceChecklistSchema).sort((a, b) =>
      a.stakeholderType.localeCompare(b.stakeholderType)
    );
  },

  async createChecklist(data: {
    insurerName: string;
    stakeholderType: StakeholderType;
    serviceAreaIds?: string[];
    items: ChecklistItem[];
    reportThresholds?: number[];
  }): Promise<InsuranceChecklist> {
    const docRef = doc(collection(db, COLLECTIONS.INSURANCE_CHECKLISTS));
    const now = serverTimestamp();
    const payload = {
      insurerName: data.insurerName,
      // Agent-created models are advocated by the agent's insurer.
      sponsor: data.insurerName,
      stakeholderType: data.stakeholderType,
      ...(data.serviceAreaIds?.length ? { serviceAreaIds: data.serviceAreaIds } : {}),
      ...(data.reportThresholds?.length ? { reportThresholds: data.reportThresholds } : {}),
      isDefault: false,
      items: data.items,
      createdAt: now,
      updatedAt: now,
    };
    await setDoc(docRef, payload);
    return {
      id: docRef.id,
      insurerName: data.insurerName,
      sponsor: data.insurerName,
      stakeholderType: data.stakeholderType,
      serviceAreaIds: data.serviceAreaIds,
      isDefault: false,
      items: data.items,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
  },

  async updateChecklist(
    checklistId: string,
    data: {
      stakeholderType?: StakeholderType;
      serviceAreaIds?: string[];
      items?: ChecklistItem[];
      reportThresholds?: number[];
    }
  ): Promise<void> {
    const docRef = doc(db, COLLECTIONS.INSURANCE_CHECKLISTS, checklistId);
    const update: Record<string, unknown> = { updatedAt: serverTimestamp() };
    if (data.stakeholderType !== undefined) update.stakeholderType = data.stakeholderType;
    if (data.serviceAreaIds !== undefined) update.serviceAreaIds = data.serviceAreaIds;
    if (data.items !== undefined) update.items = data.items;
    if (data.reportThresholds !== undefined) update.reportThresholds = data.reportThresholds;
    await updateDoc(docRef, update);
  },

  async deleteChecklist(checklistId: string): Promise<void> {
    const docRef = doc(db, COLLECTIONS.INSURANCE_CHECKLISTS, checklistId);
    await deleteDoc(docRef);
  },

  // Returns the count of structures assigned to this checklist (capped at 100). Used for delete confirmation warnings.
  async countStructuresUsingChecklist(checklistId: string): Promise<number> {
    // insurerChecklistId lives on structureClaims, not structures — querying
    // the structures collection silently returned 0 and made every
    // delete-confirm read as unused.
    const q = query(
      collection(db, COLLECTIONS.STRUCTURE_CLAIMS),
      where('insurerChecklistId', '==', checklistId),
      limit(100)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  },

  async getStructureProgress(
    structureId: string,
    checklistId: string
  ): Promise<ChecklistProgress | null> {
    const docId = `${structureId}_${checklistId}`;
    const docRef = doc(db, COLLECTIONS.STRUCTURE_PROGRESS, docId);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) {
      return null;
    }

    const parsed = ChecklistProgressSchema.safeParse(snapshot.data());
    return parsed.success ? parsed.data : null;
  },

  // KNOWN LIMITATION: this (and uncompleteItem) is a non-atomic
  // read-modify-write of the whole completedItems array. Two sessions
  // toggling concurrently (owner + agent) can silently drop each other's
  // change. arrayUnion/arrayRemove can't express "replace the entry for
  // this itemId" on object entries, so the real fix is a server-side
  // transaction (Cloud Function) — acceptable risk for the single-owner
  // Phase 1 usage pattern.
  async completeItem(
    structureId: string,
    checklistId: string,
    itemId: string,
    photoUrl?: string,
    photoPath?: string,
    notes?: string
  ): Promise<void> {
    const docId = `${structureId}_${checklistId}`;
    const docRef = doc(db, COLLECTIONS.STRUCTURE_PROGRESS, docId);

    const newEntry = buildCompletedItemEntry(itemId, photoUrl, photoPath, notes);
    const existing = await getDoc(docRef);

    if (existing.exists()) {
      const parsed = ChecklistProgressSchema.safeParse(existing.data());
      if (!parsed.success) {
        throw new AppError('Checklist progress document is malformed.', 'VALIDATION_ERROR');
      }
      const completedItems = parsed.data.completedItems.filter((item) => item.itemId !== itemId);
      completedItems.push(newEntry);

      await updateDoc(docRef, {
        completedItems,
        updatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(docRef, {
        structureId,
        checklistId,
        completedItems: [newEntry],
        updatedAt: serverTimestamp(),
      });
    }
  },

  // Seed many completed items in one write — the try-then-save preview's
  // "apply my local ticks after sign-up" path. Merges with any existing
  // progress doc (unclaim keeps progress docs around; a re-claimer's old
  // ticks must survive) via the same non-atomic read-modify-write pattern as
  // completeItem, with the same accepted single-owner risk. Returns the
  // merged completed count for the caller's progress-snapshot write.
  async seedProgress(structureId: string, checklistId: string, itemIds: string[]): Promise<number> {
    const docId = `${structureId}_${checklistId}`;
    const docRef = doc(db, COLLECTIONS.STRUCTURE_PROGRESS, docId);

    const existing = await getDoc(docRef);
    let existingItems: CompletedItemEntry[] = [];
    if (existing.exists()) {
      const parsed = ChecklistProgressSchema.safeParse(existing.data());
      if (!parsed.success) {
        throw new AppError('Checklist progress document is malformed.', 'VALIDATION_ERROR');
      }
      existingItems = parsed.data.completedItems;
    }
    const already = new Set(existingItems.map((item) => item.itemId));
    const completedItems = [
      ...existingItems,
      ...itemIds.filter((id) => !already.has(id)).map((id) => buildCompletedItemEntry(id)),
    ];

    if (existing.exists()) {
      await updateDoc(docRef, { completedItems, updatedAt: serverTimestamp() });
    } else {
      await setDoc(docRef, {
        structureId,
        checklistId,
        completedItems,
        updatedAt: serverTimestamp(),
      });
    }
    return completedItems.length;
  },

  async uncompleteItem(structureId: string, checklistId: string, itemId: string): Promise<void> {
    const docId = `${structureId}_${checklistId}`;
    const docRef = doc(db, COLLECTIONS.STRUCTURE_PROGRESS, docId);

    const existing = await getDoc(docRef);
    if (!existing.exists()) {
      return;
    }

    const parsed = ChecklistProgressSchema.safeParse(existing.data());
    if (!parsed.success) {
      throw new AppError('Checklist progress document is malformed.', 'VALIDATION_ERROR');
    }
    const completedItems = parsed.data.completedItems.filter((item) => item.itemId !== itemId);

    await updateDoc(docRef, {
      completedItems,
      updatedAt: serverTimestamp(),
    });
  },
};
